import { FOLDER_NAME_MAX_LENGTH } from "@frontend-shared/constants";
import { requiredString } from "@frontend-shared/schema/field-builders";
import type { IChatFolderListObject } from "@frontend-shared/types";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { StyledInput } from "@/components/StyledInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

interface IRenameChatFolderDialogProps {
  folder: IChatFolderListObject;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onRename: (name: string) => Promise<void>;
}

export const RenameChatFolderDialog: React.FC<IRenameChatFolderDialogProps> = ({
  folder,
  isOpen,
  onOpenChange,
  onRename,
}) => {
  const { t } = useTranslation("sidebar");

  const formSchema = z.object({
    name: requiredString({
      requiredMessage: t("chat_folder_list.rename_folder_dialog.required"),
      maxLength: FOLDER_NAME_MAX_LENGTH,
      maxLengthMessage: t("chat_folder_list.rename_folder_dialog.max_length", {
        max: FOLDER_NAME_MAX_LENGTH,
      }),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    ...FORM_VALIDATION_OPTIONS,
    resolver: zodResolver(formSchema),
    defaultValues: { name: folder.name },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({ name: folder.name });
    }
  }, [isOpen, folder.name, form]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (
    values: z.infer<typeof formSchema>,
  ): Promise<void> => {
    if (values.name.trim() === folder.name) {
      onOpenChange(false);
      return;
    }
    setIsSubmitting(true);
    try {
      await onRename(values.name.trim());
      onOpenChange(false);
    } catch {
      // NOTE: Dialog stays open so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("chat_folder_list.rename_folder_dialog.title")}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormControl>
                    <StyledInput {...field} isError={!!error} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                className="grow"
                onClick={() => onOpenChange(false)}
              >
                {t("chat_folder_list.rename_folder_dialog.cancel")}
              </Button>
              <Button
                type="submit"
                className="grow"
                disabled={!form.formState.isDirty || isSubmitting}
              >
                {t("chat_folder_list.rename_folder_dialog.rename")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
